import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static targets = [
    "form",
    "paymentElement",
    "submit",
    "spinner",
    "buttonText",
    "message",
    "selectedPaymentMethodId",
    "paymentMethodSelect",
    "addNewLink",
    "newPaymentSection",
    "creditCardSection",
    "savedPaymentSection",
    "paymentType",
    "directedGivingSection",
    "directedGivingForm",
    "directedGivingButton",
    "directedGivingFundName",
    "walletAccordionRow",
    "walletAccordionHeader",
    "walletAccordionBody",
    "walletRadio",
    "dafAccordionHeader",
    "dafAccordionBody",
    "dafRadio",
    "feeCoverageSection",
    "feeCoverageCheckbox",
    "feeCoveragePreview",
    "feeCoverageBaseAmount"
  ]

  static values = {
    publishableKey: String,
    returnUrl: String,
    totalAmount: Number,
    clientSecret: String,
    intentId: String,
    vip: Boolean
  }

  async connect() {
    console.log('connect stripe payments controller')
    this.submitting = false
    this.isFormValid = false
    this.usingSavedMethod = false
    this.selectedPaymentMethodId = null
    this.usingDirectedGiving = false
    this.isAchPayment = null // null so first change event triggers capture_method update
    this.defaultButtonText = 'REVIEW DONATION'

    // VIP mode: only DAF payment, skip Stripe initialization
    if (this.vipValue) {
      console.log('Payment: VIP mode - DAF only')
      this.usingDirectedGiving = true
      this.updatePaymentMethodType('directed_giving')
      const submitButton = this.getSubmitButton()
      if (submitButton) submitButton.disabled = false
      return
    }

    // Check if we have saved payment methods selected by default
    this.initializeSavedMethodSelection()

    // Guard against missing Stripe.js or publishable key
    if (typeof Stripe === 'undefined') {
      console.error('Stripe.js not loaded')
      this.showMessage('Payment system not available. Please refresh the page.')
      return
    }

    if (!this.publishableKeyValue) {
      console.error('No Stripe publishable key provided')
      this.showMessage('Payment configuration error. Please contact support.')
      return
    }

    this.stripe = Stripe(this.publishableKeyValue)
    this.initialize()
  }

  // Get the submit button - may be outside controller scope on mobile due to navigation repositioning
  getSubmitButton() {
    if (this.hasSubmitTarget) {
      return this.submitTarget
    }
    // Fallback: search in mobile nav placeholder
    const mobileNav = document.getElementById('mobile-nav-placeholder')
    if (mobileNav) {
      return mobileNav.querySelector('[data-payment-target="submit"]')
    }
    return null
  }

  async initialize() {
    // Guard against stripe not being initialized
    if (!this.stripe) {
      console.error('Stripe not initialized')
      return
    }

    // Use the payment intent that was already created server-side
    // This is more secure as the amount is set server-side and can't be tampered with
    const clientSecret = this.clientSecretValue
    this.paymentIntentId = this.intentIdValue

    if (!clientSecret) {
      console.error('No client secret provided')
      this.showMessage('Payment initialization failed. Please refresh the page.')
      return
    }

    // Stripe Elements appearance matching our design system
    const appearance = {
      theme: 'stripe',
      variables: {
        colorPrimary: '#EE6840',
        colorBackground: '#ffffff',
        colorText: '#2C3441',
        colorDanger: '#dc3545',
        fontFamily: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSizeBase: '15px',
        borderRadius: '5px',
        spacingUnit: '4px',
        spacingGridRow: '16px',
        spacingGridColumn: '16px'
      },
      rules: {
        '.Input': {
          border: '1px solid #CACCCF',
          boxShadow: 'none',
          padding: '12px',
          fontSize: '15px',
          lineHeight: '1.5'
        },
        '.Input:focus': {
          border: '1px solid #EE6840',
          boxShadow: '0 0 0 0.2rem rgba(238, 104, 64, 0.15)'
        },
        '.Input--invalid': {
          border: '1px solid #dc3545'
        },
        '.Label': {
          color: '#95999F',
          fontSize: '12px',
          fontWeight: '400',
          marginBottom: '4px'
        },
        '.Tab': {
          border: '1px solid #CACCCF',
          borderRadius: '5px'
        },
        '.Tab:hover': {
          border: '1px solid #EE6840'
        },
        '.Tab--selected': {
          border: '1px solid #EE6840',
          backgroundColor: '#FFF5F2'
        },
        '.Error': {
          color: '#dc3545',
          fontSize: '13px'
        }
      }
    }

    this.elements = this.stripe.elements({
      clientSecret,
      appearance,
      paymentMethodCreation: 'manual'
    })

    const paymentElementOptions = {
      layout: "accordion",
      paymentMethodOrder: ['card', 'us_bank_account'],
      // Disable wallets in Payment Element - we use Express Checkout Element for wallets
      wallets: {
        applePay: 'never',
        googlePay: 'never'
      },
      defaultValues: {
        billingDetails: this.getBillingDetails()
      }
    }

    this.paymentElement = this.elements.create("payment", paymentElementOptions)
    this.paymentElement.mount("#payment-element")

    // Disable submit button initially, unless a saved payment method is already selected
    if (!this.usingSavedMethod) {
      const submitButton = this.getSubmitButton()
      if (submitButton) submitButton.disabled = true
    }

    // Listen for changes in the Payment Element to validate
    this.paymentElement.on('change', (event) => {
      // Close custom accordion rows when user interacts with Stripe's accordion
      // But not when we're programmatically collapsing Stripe (e.g., opening a custom accordion)
      if (!this._collapsingStripe) {
        this.closeAllCustomAccordions()
      }
      this.handleFormValidation(event)
    })
  }

  handleFormValidation(event) {
    // Get the validation status from the event
    this.isFormValid = event.complete

    // Enable or disable the submit button based on validation
    // But only if we're NOT using a saved payment method
    if (!this.usingSavedMethod) {
      const submitButton = this.getSubmitButton()
      if (submitButton) submitButton.disabled = !this.isFormValid
    }

    // Detect payment method type change (card vs bank account)
    if (event.value && event.value.type) {
      const isAch = event.value.type === 'us_bank_account'
      if (isAch !== this.isAchPayment) {
        this.isAchPayment = isAch
        this.updateButtonTextForPaymentType()
        this.updatePaymentMethodType(isAch ? 'us_bank_account' : 'card')
      }
    }

    // Show error message if there's a validation error
    if (event.error) {
      this.showMessage(event.error.message)
    } else {
      // Clear any previous error messages
      if (this.hasMessageTarget) {
        this.messageTarget.textContent = ""
        this.messageTarget.classList.add('hidden', 'd-none')
      }
    }
  }

  async handleSubmit(event) {
    event.preventDefault()
    if (this.submitting) return

    // Skip if this is a wallet payment (Apple Pay/Google Pay) - already handled
    if (this.formTarget.dataset.walletPaymentConfirmed === 'true') {
      console.log('Payment: Skipping handleSubmit - wallet payment already confirmed')
      this.formTarget.submit()
      return
    }

    // Handle directed giving: skip Stripe API calls, just submit form
    if (this.usingDirectedGiving) {
      console.log('Payment: Directed giving selected, submitting form directly')
      if (this.hasPaymentTypeTarget) {
        this.paymentTypeTarget.value = 'directed_giving'
      }
      this.formTarget.submit()
      return
    }

    // Validate: either using saved method OR new payment form is valid
    if (!this.usingSavedMethod && !this.isFormValid) {
      this.showMessage("Please complete payment details before submitting.")
      return
    }

    // Update button to show processing state
    const submitButton = this.getSubmitButton()
    if (submitButton) {
      submitButton.disabled = true
      const spinnerText = this.isAchPayment ? 'Processing...' : 'Reviewing...'
      submitButton.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> ${spinnerText}`
    }
    this.submitting = true

    try {
      // ACH payments require mandate acceptance, so we must use confirmPayment()
      // which handles the mandate UI. Cards use the existing create-attach flow
      // so the review step can confirm and capture separately.
      if (this.isAchPayment && !this.usingSavedMethod) {
        console.log('Payment: ACH payment - using confirmPayment for mandate acceptance')

        const { error, paymentIntent } = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        })

        if (error) {
          this.handleError(error)
          return
        }

        console.log('Payment: ACH confirmed, status:', paymentIntent.status)

        // Submit form with payment_intent_id so server can create the order
        const hiddenField = document.createElement('input')
        hiddenField.type = 'hidden'
        hiddenField.name = 'payment_intent_id'
        hiddenField.value = paymentIntent.id
        this.formTarget.appendChild(hiddenField)

        this.formTarget.submit()
        return
      }

      // Card/saved method flow: create PM, attach, then proceed to review
      let paymentMethodId

      if (this.usingSavedMethod && this.selectedPaymentMethodId) {
        // Using a saved payment method - just use the existing ID
        console.log('Payment: Using saved payment method', this.selectedPaymentMethodId)
        paymentMethodId = this.selectedPaymentMethodId
      } else {
        // Creating a new payment method
        console.log('Payment: Creating new payment method')

        // First, submit the elements form
        // This is required by Stripe before calling createPaymentMethod
        const { error: submitError } = await this.elements.submit();

        if (submitError) {
          this.handleError(submitError)
          return
        }

        // Then create the payment method with the validated elements
        const { error, paymentMethod } = await this.stripe.createPaymentMethod({
          elements: this.elements,
          params: {
            billing_details: this.getBillingDetails()
          }
        });

        if (error) {
          this.handleError(error)
          return
        }

        paymentMethodId = paymentMethod.id
      }

      // Attach the payment method to the payment intent via our server
      const attachResponse = await fetch("/payments/attach_payment_method", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify({
          payment_intent_id: this.paymentIntentId,
          payment_method_id: paymentMethodId
        })
      });

      if (!attachResponse.ok) {
        const errorData = await attachResponse.json();
        this.handleError({ message: errorData.error || "Failed to attach payment method" });
        return;
      }

      // If payment method attachment succeeds, proceed to review
      // We'll add a hidden field with the payment_intent_id
      const hiddenField = document.createElement('input')
      hiddenField.type = 'hidden'
      hiddenField.name = 'payment_intent_id'
      hiddenField.value = this.paymentIntentId
      this.formTarget.appendChild(hiddenField)

      // Submit the form to proceed to review
      this.formTarget.submit()

    } catch (error) {
      this.handleError(error)
    }
  }

  // Update submit button text based on payment method type
  updateButtonTextForPaymentType() {
    if (this.hasButtonTextTarget) {
      if (this.isAchPayment) {
        this.buttonTextTarget.innerHTML = 'CONFIRM DONATION <i class="fa-solid fa-arrow-right ms-2"></i>'
      } else {
        this.buttonTextTarget.innerHTML = this.defaultButtonText + ' <i class="fa-solid fa-arrow-right ms-2"></i>'
      }
    }
  }

  // Notify server of payment method type change so fees are recalculated
  updatePaymentMethodType(paymentMethodType) {
    console.log('Payment: Updating payment method type to', paymentMethodType)

    fetch('/shopping_cart/update_payment_method_type', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ payment_method_type: paymentMethodType })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        console.log('Payment: Fees updated for', paymentMethodType, data.cart)
        // Update fee coverage display with new amounts
        this.updateFeeCoverageDisplay(data.cart)
        // Show/hide fee coverage based on payment method
        this.updateFeeCoverageVisibility(paymentMethodType)
        // Dispatch cartUpdated event so purchase summary updates
        document.dispatchEvent(new CustomEvent('cartUpdated', { detail: { cart: data.cart } }))
      } else {
        console.error('Payment: Failed to update payment method type:', data.error)
      }
    })
    .catch(error => {
      console.error('Payment: Error updating payment method type:', error)
    })
  }

  // Toggle fee coverage on/off
  toggleFeeCoverage(event) {
    const isEnabled = event.target.checked
    console.log('Payment: Toggling fee coverage to', isEnabled)

    fetch('/shopping_cart/update_fee_coverage', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ fee_coverage_enabled: isEnabled })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        console.log('Payment: Fee coverage updated', data.cart)
        this.updateFeeCoverageDisplay(data.cart)
        // Update purchase summary totals
        document.dispatchEvent(new CustomEvent('cartUpdated', { detail: { cart: data.cart } }))
      } else {
        console.error('Payment: Fee coverage update failed:', data.error)
        event.target.checked = !isEnabled
      }
    })
    .catch(error => {
      console.error('Payment: Error updating fee coverage:', error)
      event.target.checked = !isEnabled
    })
  }

  // Update fee coverage display from cart data
  updateFeeCoverageDisplay(cart) {
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

    if (this.hasFeeCoveragePreviewTarget && cart.fee_coverage_amount !== undefined) {
      this.feeCoveragePreviewTarget.textContent = formatter.format(cart.fee_coverage_amount)
    }

    if (this.hasFeeCoverageBaseAmountTarget && cart.donation_amount !== undefined) {
      this.feeCoverageBaseAmountTarget.textContent = formatter.format(cart.donation_amount)
    }

    // Show/hide fee line in purchase summary
    const feeLine = document.getElementById('fee-line')
    if (feeLine) {
      feeLine.style.display = cart.fee_coverage_enabled ? '' : 'none'
    }

    // Update fee amount in purchase summary
    const feeAmount = document.getElementById('cart-fee')
    if (feeAmount && cart.fee_coverage_amount !== undefined) {
      feeAmount.textContent = formatter.format(cart.fee_coverage_amount)
    }

    // Update grand total in purchase summary
    const cartTotal = document.getElementById('cart-total')
    if (cartTotal && cart.total !== undefined) {
      cartTotal.textContent = formatter.format(cart.total)
    }
  }

  // Show/hide fee coverage section based on payment method
  updateFeeCoverageVisibility(paymentMethodType) {
    if (!this.hasFeeCoverageSectionTarget) return

    if (paymentMethodType === 'directed_giving') {
      this.feeCoverageSectionTarget.classList.add('d-none')
    } else {
      this.feeCoverageSectionTarget.classList.remove('d-none')
    }
  }

  getBillingDetails() {
    const form = this.formTarget
    return {
      name: form.dataset.customerName,
      email: form.dataset.customerEmail,
      address: {
        line1: form.dataset.addressLine1,
        line2: form.dataset.addressLine2,
        city: form.dataset.city,
        state: form.dataset.state,
        postal_code: form.dataset.zipCode,
        country: 'US'
      }
    }
  }

  handleError(error) {
    // Reset button state
    this.restoreButtonState();

    // Get the error message
    const message = error.type === "card_error" || error.type === "validation_error" ?
      error.message : "An unexpected error occurred.";

    // Make sure the message element exists and is accessible
    if (this.hasMessageTarget) {
      this.messageTarget.textContent = message;
      this.messageTarget.classList.remove('hidden', 'd-none');

      // Log for debugging
      console.error("Payment error:", error);
    } else {
      // Fallback if target not found
      console.error("Payment error (no target):", error, message);
      alert("Payment error: " + message);
    }
  }

  showMessage(messageText) {
    const messageTarget = this.messageTarget
    messageTarget.classList.remove("hidden", "d-none")
    messageTarget.textContent = messageText

    setTimeout(() => {
      messageTarget.classList.add("hidden", "d-none")
      messageTarget.textContent = ""
    }, 4000)
  }

  setLoading(isLoading) {
    this.submitting = isLoading
    const submitButton = this.getSubmitButton()
    if (submitButton) {
      submitButton.disabled = isLoading

      if (isLoading) {
        // Store original button content if needed
        this._originalButtonHTML = submitButton.innerHTML;
        // Set to processing state
        submitButton.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Processing...';
      } else {
        // Restore original button content
        this.restoreButtonState();
      }
    }
  }

  restoreButtonState() {
    // Reset submitting flag
    this.submitting = false;

    const submitButton = this.getSubmitButton()
    if (submitButton) {
      // If we have the original HTML saved, restore it
      if (this._originalButtonHTML) {
        submitButton.innerHTML = this._originalButtonHTML;
        this._originalButtonHTML = null;
      } else {
        // Fallback if original HTML wasn't saved
        submitButton.innerHTML = 'Submit Payment';
      }

      // Enable/disable based on form validation or saved method selection
      submitButton.disabled = !(this.isFormValid || this.usingSavedMethod);
    }
  }

  // Initialize saved payment method selection if any exist
  initializeSavedMethodSelection() {
    if (this.hasPaymentMethodSelectTarget) {
      // Initialize select2 on the payment method dropdown
      const $select = $(this.paymentMethodSelectTarget)
      $select.select2({
        minimumResultsForSearch: Infinity, // Disable search
        width: '100%'
      })

      // Handle select2 change event
      $select.on('select2:select', (e) => {
        this.handlePaymentMethodChange({ target: e.target })
      })

      // Initialize with current selection
      const selectedValue = $select.val()
      if (selectedValue && selectedValue !== 'new') {
        this.usingSavedMethod = true
        this.selectedPaymentMethodId = selectedValue
        if (this.hasSelectedPaymentMethodIdTarget) {
          this.selectedPaymentMethodIdTarget.value = selectedValue
        }
        // Enable submit button since a saved method is selected
        const submitButton = this.getSubmitButton()
        if (submitButton) {
          submitButton.disabled = false
        }
        console.log('Payment: Initialized with saved method', this.selectedPaymentMethodId)
      }
    }
  }

  // Handle payment method dropdown change
  handlePaymentMethodChange(event) {
    const selectedValue = event.target.value

    // User selected a saved payment method
    this.usingSavedMethod = true
    this.selectedPaymentMethodId = selectedValue
    if (this.hasSelectedPaymentMethodIdTarget) {
      this.selectedPaymentMethodIdTarget.value = selectedValue
    }

    // Enable submit button
    const submitButton = this.getSubmitButton()
    if (submitButton) submitButton.disabled = false

    // Saved payment methods are always cards, update fees accordingly
    this.isAchPayment = false
    this.updatePaymentMethodType('card')
    this.updateFeeCoverageVisibility('card')

    console.log('Payment: Selected saved method', selectedValue)
  }

  // Show the new payment method form
  showNewPaymentForm(event) {
    event.preventDefault()

    // Clear saved method selection
    this.usingSavedMethod = false
    this.selectedPaymentMethodId = null
    if (this.hasSelectedPaymentMethodIdTarget) {
      this.selectedPaymentMethodIdTarget.value = ''
    }

    // Hide saved payment methods section and show new payment form
    if (this.hasPaymentMethodSelectTarget) {
      this.paymentMethodSelectTarget.closest('.gv-saved-payment-methods').classList.add('d-none')
    }
    if (this.hasCreditCardSectionTarget) {
      this.creditCardSectionTarget.classList.remove('d-none')
    }
    if (this.hasNewPaymentSectionTarget) {
      this.newPaymentSectionTarget.classList.remove('d-none')
    }

    // Disable submit button until new payment form is valid
    const submitBtn = this.getSubmitButton()
    if (submitBtn) submitBtn.disabled = !this.isFormValid

    console.log('Payment: Showing new payment form')
  }

  // Close all custom accordion sections (wallets and DAF)
  // Called when user interacts with Stripe Payment Element (card/ACH tabs)
  closeAllCustomAccordions() {
    if (this.hasWalletAccordionBodyTarget) {
      this.closeAccordionSection(this.walletAccordionBodyTarget, this.hasWalletRadioTarget ? this.walletRadioTarget : null)
    }
    if (this.hasDafAccordionBodyTarget) {
      const wasDafOpen = !this.dafAccordionBodyTarget.classList.contains('d-none')
      this.closeAccordionSection(this.dafAccordionBodyTarget, this.hasDafRadioTarget ? this.dafRadioTarget : null)
      // Restore fee coverage visibility if DAF was open
      if (wasDafOpen) {
        this.updateFeeCoverageVisibility('card')
      }
    }
    // Clear saved previous state since Stripe element is now active
    this.previousIsAchPayment = undefined
  }

  // Collapse Stripe's Payment Element accordion
  collapseStripeAccordion() {
    if (this.paymentElement) {
      this._collapsingStripe = true
      this.paymentElement.collapse()
      // Reset flag after Stripe's change event has fired
      setTimeout(() => { this._collapsingStripe = false }, 100)
    }
  }

  // Close a custom accordion section
  closeAccordionSection(bodyTarget, radioTarget) {
    if (bodyTarget) bodyTarget.classList.add('d-none')
    if (radioTarget) radioTarget.classList.remove('gv-accordion-radio--selected')
  }

  // Open a custom accordion section
  openAccordionSection(bodyTarget, radioTarget) {
    if (bodyTarget) bodyTarget.classList.remove('d-none')
    if (radioTarget) radioTarget.classList.add('gv-accordion-radio--selected')
  }

  // Toggle the wallet accordion row open/closed (closes DAF if open)
  toggleWalletAccordion(event) {
    event.preventDefault()

    if (this.hasWalletAccordionBodyTarget) {
      const isOpen = !this.walletAccordionBodyTarget.classList.contains('d-none')

      if (isOpen) {
        this.closeAccordionSection(this.walletAccordionBodyTarget, this.hasWalletRadioTarget ? this.walletRadioTarget : null)
        // Restore fees when closing wallet (wallet uses card rates, but ACH may have been selected before)
        if (this.previousIsAchPayment !== undefined) {
          this.isAchPayment = this.previousIsAchPayment
          this.previousIsAchPayment = undefined
        }
        const pmType = this.isAchPayment ? 'us_bank_account' : 'card'
        this.updatePaymentMethodType(pmType)
        this.updateFeeCoverageVisibility(pmType)
      } else {
        // Close DAF and collapse Stripe accordion
        if (this.hasDafAccordionBodyTarget) {
          this.closeAccordionSection(this.dafAccordionBodyTarget, this.hasDafRadioTarget ? this.dafRadioTarget : null)
        }
        this.collapseStripeAccordion()
        this.openAccordionSection(this.walletAccordionBodyTarget, this.hasWalletRadioTarget ? this.walletRadioTarget : null)

        // Save ACH state and update fees to card rate (wallets use card processing fees)
        this.previousIsAchPayment = this.isAchPayment
        this.isAchPayment = false
        this.updatePaymentMethodType('card')
      }
    }
  }

  // Toggle the DAF accordion row open/closed (closes wallet if open)
  toggleDafAccordion(event) {
    event.preventDefault()

    if (this.hasDafAccordionBodyTarget) {
      const isOpen = !this.dafAccordionBodyTarget.classList.contains('d-none')

      if (isOpen) {
        this.closeAccordionSection(this.dafAccordionBodyTarget, this.hasDafRadioTarget ? this.dafRadioTarget : null)

        // Restore previous fees when closing DAF without confirming
        if (this.previousIsAchPayment !== undefined) {
          this.isAchPayment = this.previousIsAchPayment
          this.previousIsAchPayment = undefined
        }
        this.updatePaymentMethodType(this.isAchPayment ? 'us_bank_account' : 'card')
      } else {
        // Close wallet and collapse Stripe accordion
        if (this.hasWalletAccordionBodyTarget) {
          this.closeAccordionSection(this.walletAccordionBodyTarget, this.hasWalletRadioTarget ? this.walletRadioTarget : null)
        }
        this.collapseStripeAccordion()
        this.openAccordionSection(this.dafAccordionBodyTarget, this.hasDafRadioTarget ? this.dafRadioTarget : null)

        // Update fees to zero (directed giving has no processing fees)
        this.previousIsAchPayment = this.isAchPayment
        this.isAchPayment = false
        this.updatePaymentMethodType('directed_giving')
      }
    }
  }

  // Show saved payment methods (go back from new payment form)
  showSavedPaymentMethods(event) {
    event.preventDefault()

    // Restore saved method selection from dropdown
    if (this.hasPaymentMethodSelectTarget) {
      const selectedValue = this.paymentMethodSelectTarget.value
      if (selectedValue) {
        this.usingSavedMethod = true
        this.selectedPaymentMethodId = selectedValue
        if (this.hasSelectedPaymentMethodIdTarget) {
          this.selectedPaymentMethodIdTarget.value = selectedValue
        }
      }

      // Show saved payment methods section
      this.paymentMethodSelectTarget.closest('.gv-saved-payment-methods').classList.remove('d-none')
    }

    // Hide new payment form
    if (this.hasCreditCardSectionTarget) {
      this.creditCardSectionTarget.classList.add('d-none')
    }
    if (this.hasNewPaymentSectionTarget) {
      this.newPaymentSectionTarget.classList.add('d-none')
    }

    // Enable submit button since a saved method is selected
    const submitButton = this.getSubmitButton()
    if (submitButton) submitButton.disabled = false

    console.log('Payment: Showing saved payment methods')
  }

  // Select directed giving payment method
  selectDirectedGiving(event) {
    if (event) event.preventDefault()
    this.usingDirectedGiving = true
    // Save ACH state so we can restore correct fees when switching back
    this.previousIsAchPayment = this.isAchPayment
    this.isAchPayment = false

    // Hide Stripe payment sections
    const paymentMethodsCard = this.element.querySelector('.gv-payment-methods-card')
    if (paymentMethodsCard) paymentMethodsCard.classList.add('d-none')

    // Show the DG confirmation form
    if (this.hasDirectedGivingFormTarget) {
      this.directedGivingFormTarget.classList.remove('d-none')
    }

    // Enable submit button
    const submitButton = this.getSubmitButton()
    if (submitButton) submitButton.disabled = false

    // Hide fee coverage section for DAF
    this.updateFeeCoverageVisibility('directed_giving')

    // Update fees (directed giving has zero processing fees)
    this.updatePaymentMethodType('directed_giving')

    console.log('Payment: Directed giving selected')
  }

  // Switch back to Stripe payment
  selectStripePayment(event) {
    if (event) event.preventDefault()
    this.usingDirectedGiving = false

    // Restore previous ACH state from before directed giving was selected
    if (this.previousIsAchPayment !== undefined) {
      this.isAchPayment = this.previousIsAchPayment
      this.previousIsAchPayment = undefined
    }

    // Show Stripe payment sections
    const paymentMethodsCard = this.element.querySelector('.gv-payment-methods-card')
    if (paymentMethodsCard) paymentMethodsCard.classList.remove('d-none')

    // Hide the DG form
    if (this.hasDirectedGivingFormTarget) {
      this.directedGivingFormTarget.classList.add('d-none')
    }

    // Restore submit button state and text based on form validity
    const submitButton = this.getSubmitButton()
    if (submitButton) {
      submitButton.disabled = !(this.isFormValid || this.usingSavedMethod)
    }
    this.updateButtonTextForPaymentType()

    // Show fee coverage section again
    const pmType = this.isAchPayment ? 'us_bank_account' : 'card'
    this.updateFeeCoverageVisibility(pmType)

    // Restore fees for the previous payment type
    this.updatePaymentMethodType(pmType)

    console.log('Payment: Switched back to Stripe payment')
  }
}
